/* Developer : Nithin
Date : 26-04-2026
changes: fixed loader behavior, moved hero banner, and added stats to material/received tabs */
import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDateFilteredLeads } from "@/hooks/useDateFilteredData";
import { useUsers } from "@/hooks";
import API from "@/lib/API";
import toast from "react-hot-toast";
import { accountService } from "../services/accountService";
import { AccountsStats } from "../components/AccountsStats";
import { useAccountsStats } from "../hooks/useAccountsStats";
import { AccountsFilterSection } from "../components/AccountsFilterSection";
import { AccountTableView } from "../components/AccountTableView";
import { AccountKanban } from "../components/AccountKanban";
import { AccountsTabs } from "../components/AccountsTabs";
import { GeneralExpensesPage } from "../components/GeneralExpensesPage.jsx";
import { AddAccount } from "../components/AddAccount";
import { ViewAccount } from "../components/ViewAccount";
import { ChangeStatusForm } from "../components/ChangeStatusForm";
import { ReceivedAmountControlPanel } from "../components/received_amount_control_panel.jsx";
import { MaterialExpensesControlPanel } from "../components/material_expenses_control_panel.jsx";
import { ReceivedAmountTable } from "../components/received_amount_table.jsx";
import { MaterialExpensesTable } from "../components/material_expenses_table.jsx";
import { CreditPaymentForm } from "../components/CreditPaymentForm.jsx";
import { ViewReceivedAmountModal } from "../components/ViewReceivedAmountModal.jsx";
import { ViewMaterialExpenseModal } from "../components/ViewMaterialExpenseModal.jsx";
import { RequestMaterialsModal } from "@/pages/verve-projects/verve-projects-flow/components/modals/RequestMaterialsModal.jsx";
import { AddMaterialExpenseModal } from "../components/AddMaterialExpenseModal.jsx";
import { InlineLoader as Loader, Pagination, ConfirmationModal } from "@/components/ui";
import { LEAD_STAGES, ACCOUNT_STAGES } from "@/constants/globalConstants";
import { useAuthStore } from "@/store/authStore";
import { showSuccessMessage, showApiError, showErrorMessage } from "@/lib/toast";
import { exportToExcel } from "@/utils/exportUtils";
import { useAccountFilters } from "../hooks/useAccountFilters";
import { useAccountActivityStats } from "../hooks/useAccountActivityStats";

export default function AccountsScreen({ activeService = "Verve" }) {
  const { user } = useAuthStore();

  // 1. Filter Management
  const {
    search, setSearch, debouncedSearch,
    view, setView,
    page, setPage,
    sortBy, setSortBy,
    filters, setFilters,
    showAdvancedFilters, setShowAdvancedFilters
  } = useAccountFilters();

  const queryParams = useMemo(() => ({
    search: debouncedSearch,
    page,
    limit: 10,
    status: filters.status !== 'all' ? filters.status : undefined,
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    source: filters.source !== 'all' ? filters.source : undefined,
    city: filters.location !== 'all' ? filters.location : undefined,
    assignedTo: filters.assignedTo !== 'all' ? filters.assignedTo : undefined,
    date: filters.date !== 'all' ? filters.date : undefined,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    sort: sortBy,
    isMovedToProject: "true"
  }), [debouncedSearch, page, filters, sortBy]);

  /* Developer : Nithin
  Date : 26-06-2026
  changes: added isMovedToProject flag for accountant access */
  const activityParams = useMemo(() => ({
    search: debouncedSearch,
    status: filters.status !== 'all' ? filters.status : undefined,
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    source: filters.source !== 'all' ? filters.source : undefined,
    location: filters.location !== 'all' ? filters.location : undefined,
    assignedTo: filters.assignedTo !== 'all' ? filters.assignedTo : undefined,
    date: filters.date,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    isMovedToProject: "true"
  }), [debouncedSearch, filters]);

  const { data: accountsData, isLoading: accountsLoading, refetch } = useDateFilteredLeads(queryParams, filters);
  const { data: activityStats } = useAccountActivityStats(activityParams);
  const { teamMembers: teamData } = useUsers({ limit: 1000 });

  const rawAccounts = Array.isArray(accountsData) ? accountsData : (accountsData?.leads || accountsData?.accounts || accountsData?.projects || []);
  const accounts = rawAccounts.map(l => ({
    ...l,
    isLocked: l.accountStatus === 'project_started'
  }));
  const totalAccounts = Array.isArray(accountsData) ? accountsData.length : (accountsData?.total || 0);

  const filterOptions = useMemo(() => {
    if (!rawAccounts.length) return { locations: [], sources: [], assignees: [] };
    const locations = [...new Set(rawAccounts.map(l => l.city).filter(Boolean))].sort();
    const sources = [...new Set(rawAccounts.map(l => l.source).filter(Boolean))].sort();
    const assigneesMap = new Map();
    rawAccounts.forEach(l => {
      const assigned = l.assignedTo?._id ? l.assignedTo : (typeof l.assignedTo === 'string' ? { _id: l.assignedTo, name: 'User' } : null);
      if (assigned) assigneesMap.set(assigned._id, assigned);
    });
    return {
      locations,
      sources,
      assignees: Array.from(assigneesMap.values()).sort((a, b) => a.name?.localeCompare(b.name))
    };
  }, [rawAccounts]);


  // Derived statistics using local hook
  const stats = useAccountsStats(rawAccounts, accounts, user);

  const team = teamData || [];

  // Filter team members by role and location for assignment
  const salesExecs = useMemo(() => {
    return team.filter(u => {
      const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
      return ["sales-exec", "sale-exec", "sales", "sales-executive"].includes(uRole);
    });
  }, [team]);

  const projectManagers = useMemo(() => {
    return team.filter(u => {
      const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
      return ["project-manager", "pm"].includes(uRole);
    });
  }, [team]);

  // 2. State for Modals
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", message: "", onConfirm: null, loading: false });
  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  const defaultTab = user && ['project-manager', 'store-manager'].includes(user.role) ? 'project_expenses' : 'accounts_master';
  const [activeAccountsTab, setActiveAccountsTab] = useState(defaultTab);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    /* Developer : Nithin
    Date : 26-06-2026
    changes: reset activeAccountsTab to defaultTab when no tab param is present */
    if (tab) {
      setActiveAccountsTab(tab);
    } else {
      setActiveAccountsTab(defaultTab);
    }
  }, [location.search, defaultTab]);

  const [isAddReceivedAmountOpen, setIsAddReceivedAmountOpen] = useState(false);
  const [isAddMaterialExpenseOpen, setIsAddMaterialExpenseOpen] = useState(false);
  const [receivedAmountProjects, setReceivedAmountProjects] = useState([]);
  const [receivedAmounts, setReceivedAmounts] = useState([]);
  const [materialExpenses, setMaterialExpenses] = useState([]);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [viewMaterialExpense, setViewMaterialExpense] = useState(null);

  useEffect(() => {
    if (activeAccountsTab === "received_amount" || isAddReceivedAmountOpen) {
      API.get("/general-expenses/projects")
        .then(res => {
          const raw = res?.data?.accounts || res?.data?.projects || res?.data || res?.accounts || [];
          setReceivedAmountProjects(raw);
        })
        .catch(err => console.error("Failed to fetch projects for received amount:", err));

      API.get("/received-amounts")
        .then(res => {
          const rawAmounts = res?.data?.data || [];
          const formatted = rawAmounts.map(item => ({
            _id: item._id,
            receiptId: item.receiptId,
            projectId: item.relatedProjectId?.customerCustomId || item.relatedProjectId?.projectId || item.relatedProjectId?.customId || "—",
            clientName: item.relatedProjectId?.customerName || item.relatedProjectId?.firstName || "—",
            projectName: item.relatedProjectId?.projectName || "—",
            project: item.relatedProjectId,
            paymentStage: item.paymentStage,
            paymentDate: item.paymentDate,
            paymentMode: item.paymentMethod,
            amountReceived: item.amount,
            balanceAmount: 0,
            status: item.status,
            transactionId: item.transactionId,
            bankName: item.bankName,
            branch: item.branch,
            upiPhone: item.upiPhone,
            receiverName: item.receiverName,
            receiverPhone: item.receiverPhone,
            chequeNumber: item.chequeNumber,
            chequeDate: item.chequeDate,
            remarks: item.remarks
          }));
          setReceivedAmounts(formatted);
        })
        .catch(err => console.error("Failed to fetch received amounts:", err));
    }

    if (activeAccountsTab === "material_expenses") {
      API.get("/general-expenses")
        .then(res => {
          let raw = res?.data?.expenses || res?.data?.data || res?.data || [];
          if (!Array.isArray(raw)) raw = [];
          
          const materialExps = raw.filter(e => e.category?.toUpperCase() === 'MATERIAL');
          const formatted = materialExps.map(req => ({
            _id: req._id,
            expenseId: req.customId || "—",
            date: req.date || req.createdAt || "—",
            projectId: req.projectDetails?.projectId || req.project?.projectId || req.project?.customerCustomId || req.project?.customId || "—",
            clientName: req.projectDetails?.customerName || req.project?.customerName || req.project?.firstName || req.project?.name || "—",
            project: req.project || req.projectDetails,
            materialName: req.reason || "—",
            category: "MATERIAL",
            quantity: 1,
            rate: req.amount || 0,
            amount: req.amount || 0,
            supplier: req.toWhomPaid || req.paidToName || "—",
            invoiceNo: req.invoiceNo || "—",
            status: req.status || "pending"
          }));
          setMaterialExpenses(formatted);
        })
        .catch(err => console.error("Failed to fetch material expenses:", err));
    }
  }, [activeAccountsTab, isAddReceivedAmountOpen]);

  /* Developer : Nithin
  Date : 26-06-2026
  changes: created dedicated stats memo for material and received amounts to match UI requests. Moved here to fix initialization error. */
  const materialStats = useMemo(() => {
    const totalItems = materialExpenses.length;
    const totalMaterialExpenses = materialExpenses.reduce((sum, item) => sum + (item.amount || 0), 0);
    const uniqueProjectsCount = new Set(materialExpenses.map(item => item.projectId).filter(id => id && id !== "—")).size;
    const avgProjectCost = uniqueProjectsCount > 0 ? totalMaterialExpenses / uniqueProjectsCount : 0;
    
    return { 
      totalProjects: uniqueProjectsCount, 
      totalMaterialExpenses, 
      avgProjectCost, 
      totalItems 
    };
  }, [materialExpenses]);

  /* Developer : Nithin
  Date : 26-06-2026
  changes: updated received amounts stats to calculate unique clients and include pending balance */
  const receivedStats = useMemo(() => {
    const count = receivedAmounts.length;
    const requestedTotal = receivedAmounts.reduce((sum, item) => sum + (item.amountReceived || 0), 0);
    const uniqueClients = new Set(receivedAmounts.map(item => item.clientName).filter(name => name && name !== "—")).size;
    return { count, requestedTotal, uniqueClients, pendingBalance: stats?.pendingBalance || 0 };
  }, [receivedAmounts, stats]);

  const activeAccount = useMemo(() => {
    if (!selectedAccount) return null;
    return accounts.find(l => l._id === selectedAccount._id) || selectedAccount;
  }, [selectedAccount, accounts]);

  // 3. Handlers
  const handleAccountSubmit = async (data) => {
    try {
      if (selectedAccount) {
        await accountService.update(selectedAccount._id, data);
        showSuccessMessage("Account updated successfully");
      } else {
        await accountService.create(data);
        showSuccessMessage("Account added successfully");
      }
      refetch();
      setIsFormOpen(false);
      setSelectedAccount(null);
    } catch (err) {
      showApiError(err);
    }
  };

  const handleUpdateAccount = async (id, data) => {
    try {
      const response = await accountService.update(id, data);
      const emailStatus = response.data?.emailStatus || response.emailStatus;

      if (emailStatus === 'sent') {
        showSuccessMessage("Account updated & Quotation sent via email!");
      } else if (emailStatus === 'invalid_address') {
        showErrorMessage("Account updated but Address not found! Please check the email address.");
      } else if (emailStatus === 'skipped') {
        showSuccessMessage("Account updated (Email skipped: invalid address). You can download it manually from the View modal.");
      } else if (emailStatus === 'failed') {
        showErrorMessage("Account updated but email delivery failed. Please download manually from the View modal.");
      } else {
        showSuccessMessage("Account updated");
      }
      refetch();
    } catch (err) {
      showApiError(err);
    }
  };

  const toggleAccountStatus = (account, targetStatus) => {
    if (account.isLocked) {
      showErrorMessage("Account status is locked");
      return;
    }
    const formStages = ['appointment_scheduled', 'site_visit', 'inspection_done', 'quotation_sent', 'approved', 'project_started', 'assigned', 'drop'];
    if (formStages.includes(targetStatus)) {
      setPendingStatus({ account, targetStatus });
    } else {
      handleUpdateAccount(account._id, { status: targetStatus });
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const res = await accountService.getAccounts({
        ...queryParams,
        limit: 10000,
        page: 1
      });

      const data = res?.data || res;
      const exportAccounts = Array.isArray(data) ? data : (data?.accounts || data?.projects || data?.leads || []);

      const exportData = exportAccounts.map(l => ({
        "Verve ID": (l.customId || "").toUpperCase() || "—",
        "Customer Name": l.customerName || "",
        "First Name": l.firstName || "",
        "Last Name": l.lastName || "",
        "Phone Number": l.phoneNumber || l.phone || "",
        "Email Address": l.email || "",
        "Address Line 1": l.addressLine1 || "",
        "Address Line 2": l.addressLine2 || "",
        "City": l.city || "",
        "District": l.district || "",
        "State": l.state || "",
        "PIN Code": l.pincode || "",
        "Office Location": l.officeLocation || "",
        "Reference Type": l.referenceType || "",
        "Referral Name / Details": l.referenceDetails || "",
        "Requirement Type": l.requirementType || "",
        "Type": l.propertyType || "",
        "Appointment Date": l.appointmentDate ? new Date(l.appointmentDate).toLocaleDateString('en-IN') : "—",
        "Floor Details": l.floorDetails || "",
        "Verve Capacity Requested": l.verveCapacityRequested || "",
        "Existing Power Paid": l.existingPowerPaid || "",
        "Inspection Note": l.inspectionNote || "",
        "Verve Capacity": l.verveCapacity || "",
        "Quotation Note": l.quotationNote || "",
        "Estimated Budget": l.budget || l.projectValue || 0,
        "Quotation Amount": l.quotationAmount || 0,
        "Target Cost": l.targetCost || 0,
        "Advance Paid": l.advancePaid || 0,
        "Advance Payment Status": l.advancePaymentStatus || "—",
        "Total Received": l.totalReceived || 0,
        "Other Expenses": l.otherExpenses || 0,
        "Material Expenses": l.materialExpenses || 0,
        "Balance": l.balance || 0,
        "Project Status": l.status?.replace(/_/g, ' ').toUpperCase() || "NEW",
        "Lead Status": l.leadStatus || "—",
        "Design Status": l.designStatus || "—",
        "Payment Status": l.paymentStatus || "Pending",
        "Expense Status": l.expenseStatus || "Pending",
        "Source": l.source || "Manual",
        "Created At": l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : "—",
        "Telecaller": l.telecaller?.name || l.telecallerId?.name || "—",
        "Sales Person": l.salesPerson?.name || l.salesPersonId?.name || "—",
        "Assigned PM": l.projectManager?.name || l.projectManagerId?.name || "—"
      }));

      exportToExcel(exportData, {
        filename: `Verve_Accounts_Export_${new Date().toLocaleDateString().replace(/\//g, '-')}`
      });
      showSuccessMessage("Export successful!");
    } catch (err) {
      showApiError(err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportReceivedAmounts = async () => {
    if (isExporting || !receivedAmounts || receivedAmounts.length === 0) return;
    try {
      setIsExporting(true);
      const exportData = receivedAmounts.map(r => ({
        "Receipt ID": r.receiptId || r._id.toString().slice(-6).toUpperCase(),
        "Date": r.paymentDate ? new Date(r.paymentDate).toLocaleDateString('en-IN') : "—",
        "Project ID": r.projectId || "—",
        "Client Name": r.clientName || "—",
        "Project Name": r.projectName !== "—" ? r.projectName : "",
        "Amount (₹)": r.amountReceived || 0,
        "Payment Stage": r.paymentStage || "—",
        "Payment Mode": r.paymentMode || "—",
        "Status": r.status?.toUpperCase() || "PENDING",
        "Transaction ID": r.transactionId || "—",
        "Bank Name": r.bankName || "—",
        "Branch": r.branch || "—",
        "UPI Phone": r.upiPhone || "—",
        "Cheque Number": r.chequeNumber || "—",
        "Cheque Date": r.chequeDate || "—",
        "Receiver Name": r.receiverName || "—",
        "Receiver Phone": r.receiverPhone || "—",
        "Remarks": r.remarks || "—"
      }));
      await exportToExcel(exportData, { filename: `Received_Amounts_${new Date().toISOString().split('T')[0]}` });
    } catch (err) {
      console.error("Failed to export received amounts:", err);
      toast.error("Failed to export received amounts");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportMaterialExpenses = async () => {
    if (isExporting || !materialExpenses || materialExpenses.length === 0) return;
    try {
      setIsExporting(true);
      const exportData = materialExpenses.map(r => ({
        "Expense ID": r.expenseId || "—",
        "Date": r.date ? new Date(r.date).toLocaleDateString('en-IN') : "—",
        "Project ID": r.projectId || "—",
        "Client Name": r.clientName || "—",
        "Material Name": r.materialName || "—",
        "Category": r.category || "—",
        "Quantity": r.quantity || 0,
        "Rate (₹)": r.rate || 0,
        "Amount (₹)": r.amount || 0,
        "Supplier": r.supplier || "—",
        "Invoice No": r.invoiceNo || "—",
        "Status": r.status || "—"
      }));
      await exportToExcel(exportData, { filename: `Material_Expenses_${new Date().toISOString().split('T')[0]}` });
    } catch (err) {
      console.error("Failed to export material expenses:", err);
      toast.error("Failed to export material expenses");
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setFilters({
      status: 'all',
      activityStatus: 'all',
      source: 'all',
      city: 'all',
      assignedTo: 'all',
      date: 'all',
      fromDate: '',
      toDate: ''
    });
  };

  const renderTabs = () => (
    <AccountsTabs
      activeTab={activeAccountsTab}
      onTabChange={setActiveAccountsTab}
    />
  );

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">

      {/* Brand Hero Banner */}
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between mt-[-10px]">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">Verve Accounts</h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Accounts Management</p>
        </div>
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/1.jpg"
            alt="Account Management"
            className="absolute top-0 right-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>

      {activeAccountsTab === "accounts_master" && (
        <div className="flex flex-col gap-5 w-full min-w-0 max-w-full">
          <AccountsStats
            stats={stats}
            isExpanded={isStatsExpanded}
            onToggle={() => setIsStatsExpanded(!isStatsExpanded)}
          />
          
          {/* Developer : Nithin
          Date : 26-06-2026
          changes: combined tabs and filter section into a single flex container with gap-2 to match material expenses layout */}
          <div className="flex flex-col gap-2">
            {renderTabs()}
            <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
              <AccountsFilterSection
                search={search} setSearch={setSearch}
                filters={filters} setFilters={setFilters}
                sortBy={sortBy} setSortBy={setSortBy}
                showAdvancedFilters={showAdvancedFilters} setShowAdvancedFilters={setShowAdvancedFilters}
                view={view} setView={setView}
                onAddClick={() => { setSelectedAccount(null); setIsFormOpen(true); }}
                onExport={handleExport}
                isExporting={isExporting}
                filterOptions={filterOptions}
                activityStats={activityStats}
                onReset={handleReset}
              />
            </div>
          </div>

          {/* Developer : Nithin
          Date : 26-06-2026
          changes: removed min-h-[400px] to fix unnecessary vertical scrollbar on empty/small content */}
          <div className="flex-1 w-full min-h-0">
            {view === "table" ? (
              <AccountTableView
                accounts={accounts}
                isLoading={accountsLoading && !accounts.length}
                onAccountClick={(account) => { setSelectedAccount(account); setIsDetailOpen(true); }}
                onEditClick={(account) => { setSelectedAccount(account); setIsFormOpen(true); }}
                onAddActivity={(accountId, note, type) => {
                  // Activity handled here
                }}
                onToggleDisable={async (account) => {
                  const isEnabling = account.isDisabled;
                  setConfirmState({
                    isOpen: true,
                    title: isEnabling ? "Enable Account" : "Disable Account",
                    message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this account record? ${isEnabling ? 'This will make it visible in the active pipeline.' : 'This will move it to a restricted view and pause interactions.'}`,
                    variant: isEnabling ? "info" : "danger",
                    confirmText: isEnabling ? "Enable Record" : "Disable Record",
                    onConfirm: async () => {
                      setConfirmState(prev => ({ ...prev, loading: true }));
                      try {
                        await accountService.toggleDisable(account._id, !account.isDisabled);
                        showSuccessMessage(`Account ${isEnabling ? 'enabled' : 'disabled'} successfully`);
                        refetch();
                      } catch (err) {
                        showApiError(err);
                      } finally {
                        setConfirmState({ isOpen: false });
                      }
                    }
                  });
                }}
                updateStatus={toggleAccountStatus}
                onUpdateAccount={handleUpdateAccount}
                user={user}
                teamMembers={team}
                salesExecs={salesExecs}
                projectManagers={projectManagers}
              />
            ) : (
              <AccountKanban
                stages={ACCOUNT_STAGES}
                filtered={accounts}
                onAccountClick={(account) => { setSelectedAccount(account); setIsDetailOpen(true); }}
                updateStatus={toggleAccountStatus}
                onUpdateAccount={handleUpdateAccount}
                user={user}
                teamMembers={team}
              />
            )}
          </div>

          {totalAccounts > 10 && (
            <Pagination
              page={page}
              totalPages={Math.ceil(totalAccounts / 10)}
              onPageChange={setPage}
            />
          )}

          <AddAccount
            isOpen={isFormOpen}
            onClose={() => { setIsFormOpen(false); setSelectedAccount(null); }}
            onSubmit={handleAccountSubmit}
            initialData={activeAccount}
            teamMembers={team}
            user={user}
          />

          <ChangeStatusForm
            isOpen={!!pendingStatus}
            onClose={() => setPendingStatus(null)}
            onConfirm={async (extraData) => {
              if (!pendingStatus?.account?._id) return;
              const response = await accountService.update(pendingStatus.account._id, { ...extraData, status: pendingStatus.targetStatus });

              const emailStatus = response.data?.emailStatus || response.emailStatus;
              if (emailStatus === 'sent') {
                showSuccessMessage("Status updated & Quotation sent!");
              } else if (emailStatus === 'invalid_address') {
                showErrorMessage("Status updated but Address not found! Please check the email address.");
              } else if (emailStatus === 'skipped') {
                showSuccessMessage("Status updated (Email skipped: No valid address). You can download and send it manually from the View modal.");
              } else if (emailStatus === 'failed') {
                showErrorMessage("Status updated but email failed to send. Please download manually from the View modal.");
              } else {
                showSuccessMessage("Status updated successfully");
              }

              setPendingStatus(null);
              refetch();
            }}
            pendingStatus={pendingStatus}
            teamMembers={team}
            stages={LEAD_STAGES}
            user={user}
          />

          <ViewAccount
            isOpen={isDetailOpen}
            onClose={() => { setIsDetailOpen(false); setSelectedAccount(null); }}
            account={activeAccount}
            user={user}
            stages={LEAD_STAGES}
            teamMembers={team}
            projectManagers={projectManagers}
            updateStatus={toggleAccountStatus}
            updateAccountState={handleUpdateAccount}
            onToggleDisable={async (account) => {
              // Re-use the same confirmation state for consistency
              const isEnabling = account.isDisabled;
              setConfirmState({
                isOpen: true,
                title: isEnabling ? "Enable Account" : "Disable Account",
                message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this account record?`,
                variant: isEnabling ? "info" : "danger",
                onConfirm: async () => {
                  setConfirmState(prev => ({ ...prev, loading: true }));
                  try {
                    await accountService.toggleDisable(account._id, !account.isDisabled);
                    showSuccessMessage(`Account ${isEnabling ? 'enabled' : 'disabled'} successfully`);
                    refetch();
                  } catch (err) { showApiError(err); } finally { setConfirmState({ isOpen: false }); }
                }
              });
            }}
          />

          <ConfirmationModal
            isOpen={confirmState.isOpen}
            onClose={() => setConfirmState({ isOpen: false })}
            onConfirm={confirmState.onConfirm}
            title={confirmState.title}
            message={confirmState.message}
            variant={confirmState.variant}
            confirmText={confirmState.confirmText}
            loading={confirmState.loading}
          />
        </div>
      )}

      {/* Developer : Nithin
      Date : 26-06-2026
      changes: removed TabsComponent for general_expenses to hide tabs entirely */}
      {activeAccountsTab === "general_expenses" && (
        <GeneralExpensesPage key="general" initialCategory="general" />
      )}

      {activeAccountsTab === "project_expenses" && (
        <GeneralExpensesPage key="project" initialCategory="project" TabsComponent={renderTabs} />
      )}

      {activeAccountsTab === "received_amount" && (
        <div className="flex flex-col gap-5 w-full min-w-0 max-w-full">
          <AccountsStats
            stats={receivedStats}
            activeTab={activeAccountsTab}
            isExpanded={isStatsExpanded}
            onToggle={() => setIsStatsExpanded(!isStatsExpanded)}
          />
          <div className="flex flex-col gap-2">
            {renderTabs()}
            <ReceivedAmountControlPanel
              search={search}
              setSearch={setSearch}
              filters={filters}
              setFilters={setFilters}
              sortBy={sortBy}
              setSortBy={setSortBy}
              view={view}
              setView={setView}
              onAddClick={() => setIsAddReceivedAmountOpen(true)}
              onExport={handleExportReceivedAmounts}
              isExporting={isExporting}
            />
          </div>
          <ReceivedAmountTable
            receipts={receivedAmounts}
            onView={setViewReceipt}
          />

          <CreditPaymentForm
            isOpen={isAddReceivedAmountOpen}
            onClose={() => setIsAddReceivedAmountOpen(false)}
            projects={receivedAmountProjects}
            onConfirm={async (data) => {
              try {
                const payload = {
                  amount: Number(data.amount),
                  paymentStage: data.stage,
                  paymentDate: data.paymentDate,
                  paymentMethod: data.method,
                  transactionId: data.transactionId,
                  bankName: data.bankName,
                  branch: data.branch,
                  upiPhone: data.upiPhone,
                  receiverName: data.receiverName,
                  receiverPhone: data.receiverPhone,
                  chequeNumber: data.chequeNumber,
                  chequeDate: data.chequeDate,
                  remarks: data.remarks,
                  relatedProjectId: data.relatedProjectId,
                  relatedProjectCollection: data.relatedProjectCollection
                };

                await API.post("/received-amounts", payload);
                toast.success("Received amount recorded successfully");
                setIsAddReceivedAmountOpen(false);
                // Optionally refetch receipts or accounts here
              } catch (err) {
                console.error("Submit Error:", err);
                toast.error(err.response?.data?.message || "Failed to record amount");
              }
            }}
          />

          <ViewReceivedAmountModal
            isOpen={!!viewReceipt}
            onClose={() => setViewReceipt(null)}
            receipt={viewReceipt}
          />
        </div>
      )}

      {activeAccountsTab === "material_expenses" && (
        <div className="flex flex-col gap-5 w-full min-w-0 max-w-full">
          <AccountsStats
            stats={materialStats}
            activeTab={activeAccountsTab}
            isExpanded={isStatsExpanded}
            onToggle={() => setIsStatsExpanded(!isStatsExpanded)}
          />
          <div className="flex flex-col gap-2">
            {renderTabs()}
            <MaterialExpensesControlPanel
              search={search}
              setSearch={setSearch}
              filters={filters}
              setFilters={setFilters}
              sortBy={sortBy}
              setSortBy={setSortBy}
              view={view}
              setView={setView}
              // onAddClick={() => setIsAddMaterialExpenseOpen(true)}
              onExport={handleExportMaterialExpenses}
              isExporting={isExporting}
            />
          </div>
          {/* Updated Halimunnisa 08-06-2026 */}

          <MaterialExpensesTable
            expenses={materialExpenses}
            onView={setViewMaterialExpense}
          />

          <AddMaterialExpenseModal
            isOpen={isAddMaterialExpenseOpen}
            onClose={() => setIsAddMaterialExpenseOpen(false)}
            projects={rawAccounts}
            user={user}
            onSuccess={() => {
              setIsAddMaterialExpenseOpen(false);
              // Refetch material expenses
              setActiveAccountsTab(""); // hack to force refetch
              setTimeout(() => setActiveAccountsTab("material_expenses"), 50);
            }}
          />
          {/*  Updated Halimunnisa 08-06-2026 */}
          <ViewMaterialExpenseModal
            isOpen={!!viewMaterialExpense}
            onClose={() => setViewMaterialExpense(null)}
            expense={viewMaterialExpense}
          />
        </div>
      )}
    </div>
  );
}
